
























































import { Component, Prop, Vue } from "vue-property-decorator";
import ConfirmationPrompt from "./components/ConfirmationPrompt.vue";
import {
  AlcoholTestData,
  AlcoholTestState,
  Gender,
  Prompt,
  resetState,
  RiskLevelForScore, SelectListOption,
  TEST_DATA
} from "@/model/Model";
import { LogEvent, ScrollToTop, UpdatedHeight } from "@/utils";
import { BannerInfo, Borough } from "@/model/Boroughs";
import { NullBanner } from "@/model/DefaultBanners";
import { UserInteractionEvent } from "@/model/UserEvents";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import RegionBanner from "@/components/RegionBanner.vue";
import { RiskLevel } from "@/model/RiskLevel";
import Boroughs from "@/model/borough-data";

let InitialOffsetTop: number = 0;
const OFFSET: { offset: number } = { offset: 0 };

@Component({
  // @ts-ignore
  components: {
    AlcoholTest: () => ({
      component: import(
        /* webpackChunkName: "alchohol-test" */ "./components/AlcoholTest.vue"
      ),
      loading: LoadingSpinner
    }),
    RegionBanner,
    ConfirmationPrompt,
    TestResults: () => ({
      component: import(
        /* webpackChunkName: "test-results" */ "./components/TestResults.vue"
      ),
      loading: LoadingSpinner
    }),
    DebugPanel: () =>
      import(
        /* webpackChunkName: "debug-panel" */ "./components/DebugPanel.vue"
      )
  }
})
export default class App extends Vue {
  @Prop({
    default() {
      return TEST_DATA;
    }
  })
  public data!: AlcoholTestData;
  @Prop({
    default() {
      return OFFSET;
    }
  })
  private offset!: { offset: number };
  private focusDisabled: boolean = false;
  private prompt: Prompt | null = null;
  private onConfirmation: (() => void) | null = null;
  private isMobile: boolean;
  private stepNumber = 0;

  constructor() {
    super();
    window.addEventListener("resize", this.onResize.bind(this));
    this.isMobile = this.onResize();
  }

  public created() {
    // Check to see if we need to pre-populate a region.
    const isInNorthEastAndNorthCumbria = window.location.href.includes("north-east-and-north-cumbria");
    const params = new URLSearchParams(window.location.search);
    if (params.has("region")) {
      const availableOptions = Boroughs.filter(
        (borough: Borough) =>
          borough.excludeFromNavigation === undefined ||
          !borough.excludeFromNavigation
      ).map((borough: Borough) => ({
        label: borough.displayName || borough.name
      }));
      const region = (params.get("region") as string)
        .replace(/ /g, "")
        .toLowerCase();

      const matchIndex = availableOptions.findIndex(({ label }) => {
        const noSpaceLabel = label.replace(/ /g, "").toLowerCase();
        return noSpaceLabel === region;
      });
      if (matchIndex !== -1) {
        this.data.questions[0].inputs[0].selected =
          availableOptions[matchIndex];

        LogEvent(
          UserInteractionEvent.SelectRegion,
          availableOptions[matchIndex].label
            .toLowerCase()
            .replace(/&mdash;/g, "")
            .replace(/ /g, "-")
            .trim()
        );
      }
    } else if (isInNorthEastAndNorthCumbria) {
      this.data.questions[0].inputs[0].selected = { label: "North East and North Cumbria" };
      LogEvent(
          UserInteractionEvent.SelectRegion,
          "North East and North Cumbria"
              .toLowerCase()
              .replace(/&mdash;/g, "")
              .replace(/ /g, "-")
              .trim()
      );
    }
  }

  public updated() {
    // Make sure any external links have tracking on them.
    const externalLinks = document.querySelectorAll(
      'a[target="_blank"]'
    ) as NodeListOf<HTMLAnchorElement>;
    for (const link of externalLinks) {
      if (link.onclick === null) {
        link.onclick = () => {
          LogEvent(UserInteractionEvent.LaunchUrl, link.href);
        };
      }
    }
    UpdatedHeight();
  }

  public mounted() {
    UpdatedHeight();
  }

  get riskLevel(): RiskLevel {
    return RiskLevelForScore(this.score);
  }

  private onResize() {
    this.isMobile = window.matchMedia(
      "(min-width: 0px) and (max-width: 767px)"
    ).matches;
    return this.isMobile;
  }

  private resetTest() {
    resetState();
    ScrollToTop();
  }

  private stepChanged(stepNumber: number) {
    this.stepNumber = stepNumber;
  }

  private get isIframed(): boolean {
    return window.parent !== window;
  }

  private get currentBoroughBannerInfo(): BannerInfo {
    // TODOif (this.data.questions)
    if (this.region.bannerInfo) {
      return !this.data.state.testCompleted
        ? this.region.bannerInfo.default
        : this.region.bannerInfo[RiskLevelForScore(this.score)];
    }
    return NullBanner;
  }

  private navigationRequested(event: string) {
    this.data.state.navRequested = () => event;
  }

  private navigationFulfilled() {
    this.data.state.navRequested = () => "";
  }

  private get navRequest(): string {
    return this.data.state.navRequested();
  }

  private get state(): AlcoholTestState {
    return this.data.state;
  }

  private get score(): number {
    return this.data.state.score(this.data.questions);
  }

  private get units(): number {
    return this.data.state.units();
  }

  private get calories(): number {
    return this.data.state.calories();
  }

  private get gender(): Gender {
    return this.data.state.gender();
  }

  private get region(): Borough {
    // console.log(this.data.state);
    return this.data.state.region();
  }

  private get focusIsDisabled(): string | null {
    return this.focusDisabled ? "focus-disabled" : null;
  }

  private get hasConfirmationPrompt(): boolean {
    return this.prompt !== null;
  }

  private cancelConfirmationPrompt() {
    this.prompt = null;
    this.onConfirmation = null;
  }

  private testCompleted() {
    this.data.state.testCompleted = true;
    LogEvent(UserInteractionEvent.CompletedQuiz, this.score);
  }

  private confirmConfirmationPrompt() {
    (this.onConfirmation as () => void)();
    this.prompt = null;
    this.onConfirmation = null;
  }

  private showConfirmationPrompt({
    prompt,
    onConfirmation
  }: {
    prompt: Prompt;
    onConfirmation: () => void;
  }) {
    this.prompt = prompt;
    this.onConfirmation = onConfirmation;
    ScrollToTop();
  }

  private enableFocus() {
    this.focusDisabled = false;
  }

  private disableFocus() {
    this.focusDisabled = true;
  }

  private get isCommissioned(): boolean {
    return !(["Other", "NorthEastAndNorthCumbria"].includes(this.region.name));
  }
}
{
  UpdatedHeight();

  window.addEventListener(
    "message",
    (event: MessageEvent) => {
      if (event.data === "parent-resized") {
        UpdatedHeight();
      } else if (typeof event.data === "object") {
        if (event.data.hasOwnProperty("height-changed")) {
          const newHeight = event.data["height-changed"];
          const app = document.getElementById("app") as HTMLDivElement;
          const currentHeight = app.offsetHeight;
          if (newHeight !== currentHeight) {
            console.log("height mismatch", newHeight, currentHeight);
            UpdatedHeight();
          }
        } else if (event.data.hasOwnProperty("offset-top")) {
          InitialOffsetTop = event.data["offset-top"] as number;
          OFFSET.offset = InitialOffsetTop + 80;
        }
      }
    },
    false
  );
}
